import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { FormAddButton } from 'common/components';
import { useSelector } from 'react-redux';
import selectorUser from '@redux/user/selectors';
import StyledModal from './traceableCommentModal/traceableCommentModal';

import {
  TraceableCommentGridHeader,
  TraceableCommentTable,
} from 'pages/traceable-comment/TraceableCommentGrid';

import CommentsIcon from 'assets/traceable-comment/comment-add.svg?react';
import {
  useCheckIsSuperAdmin,
  useCheckIsMemberSupplier,
  useGetProductFullView,
} from 'hooks';
import { useGetHelpContentFields as useGetTraceableCommentFields } from 'pages/help-content-management/hooks/useGetHelpContentFields';
import { useGetTraceableComments, useSaveTraceableComment } from './hooks';
import { useGetQaSpecDataHeader } from 'pages/qa-spec/query';
import {
  useCheckAllowViewQaCommentWorkflow,
  useCheckAllowEditComments,
} from 'pages/qa-spec/hooks';

const TraceableCommentAdd = (props) => {
  return (
    <Button
      icon={
        <CommentsIcon
          style={{ marginTop: 2, marginLeft: 4, color: '#6c757d' }}
        />
      }
      size='small'
      style={{ border: 'none' }}
      {...props}
    />
  );
};

export const TraceableCommentModal = ({ children, ...rest }) => {
  return (
    <StyledModal
      title='Comments'
      destroyOnClose
      maskClosable={false}
      width={1200}
      bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      footer={null}
      {...rest}
    >
      {children}
    </StyledModal>
  );
};

const TraceableCommentContainer = ({
  qaSpecName,
  qaSpecFieldName,
  children,
}) => {
  const [visibleComment, setVisibleComment] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [pendingDateRange, setPendingDateRange] = useState([]);
  const [localComments, setLocalComments] = useState([]);
  const [isTableUpdating, setIsTableUpdating] = useState(false);
  const { treeData } = useGetTraceableCommentFields();
  const { id: productId } = useParams();
  const userInfo = useSelector(selectorUser.makeSelectUserInfo());
  const { productFull } = useGetProductFullView({ productId });
  const isDateFiltered = dateRange?.[0] && dateRange?.[1];
  const uniqueId = useMemo(
    () => Date.now() + Math.floor(Math.random() * 1000),
    []
  );

  const { qaSpecDataHeader } = useGetQaSpecDataHeader({
    id: productFull?.productId,
    isEnabled: !!productFull?.productId,
  });

  const qaSpecStatus = qaSpecDataHeader?.status || undefined;

  const isSuperAdmin = useCheckIsSuperAdmin();
  const isMemberSupplierUser = useCheckIsMemberSupplier();
  const isQAUser = useCheckAllowViewQaCommentWorkflow();

  const getCommentByFieldNameParams = useMemo(() => {
    const params = {
      productId,
      qaSpecName,
      qaSpecFieldName,
      isSupplier: isMemberSupplierUser,
    };

    if (dateRange?.[0] && dateRange?.[1]) {
      params.fromDate = dateRange[0].endOf('day').toDate().toISOString();
      params.toDate = dateRange[1].endOf('day').toDate().toISOString();
    }

    return params;
  }, [productId, qaSpecName, qaSpecFieldName, isMemberSupplierUser, dateRange]);

  const { data, refetch, isLoading } = useGetTraceableComments(
    getCommentByFieldNameParams,
    visibleComment
  );

  useEffect(() => {
    if (visibleComment) {
      refetch();
    }
  }, [visibleComment, dateRange]);

  useEffect(() => {
    if (data && visibleComment) {
      const mapped = data?.traceableComments?.length
        ? data.traceableComments.map((comment) => ({
            ...comment,
            qaSpecName,
            qaSpecFieldName,
          }))
        : [{ id: uniqueId, isNew: true, qaSpecName, qaSpecFieldName }];
      setLocalComments(mapped);
    }
  }, [data, visibleComment]);

  const handleAddComment = () => {
    if (
      localComments.some((comment) => comment.isNew) ||
      isSuperAdmin ||
      (isQAUser && qaSpecStatus !== 'Submitted')
    ) {
      return;
    }

    const newComment = {
      id: `new-${Date.now()}`,
      productId,
      ownerId: productFull?.ownerId,
      qaSpecName,
      qaSpecFieldName,
      supplierComment: '',
      qaComment: '',
      updatedBySupplier: '',
      lastUpdatedBySupplier: '',
      updatedByQa: '',
      lastUpdatedByQa: '',
      isNew: true,
    };

    setLocalComments((prev) => [newComment, ...prev]);
  };

  const handleCancelEdit = () => {
    setLocalComments((prev) =>
      prev.length > 1
        ? prev.filter((comment) => !comment.isNew)
        : [{ id: uniqueId, isNew: true, qaSpecName, qaSpecFieldName }]
    );
  };

  const { mutate: saveComment, isPending: isSaving } = useSaveTraceableComment({
    onSuccess: () => {
      refetch().finally(() => {
        setIsTableUpdating(false);
      });
    },
    onError: (error) => {
      console.error('Error saving comment:', error);
      setIsTableUpdating(false);
    },
  });

  const handleSaveComment = (comment) => {
    if (isSuperAdmin) return;

    let payload = null;

    if (isMemberSupplierUser) {
      payload = {
        productId: parseInt(productId),
        ownerId: productFull?.ownerId,
        qaSpecName,
        qaSpecFieldName,
        supplierComment: comment.supplierComment,
        supplierUpdatedBy: userInfo?.id,
        isSupplierComment: true,
      };
    } else if (isQAUser) {
      if (comment.isNew && qaSpecStatus === 'Submitted') {
        payload = {
          productId: parseInt(productId),
          ownerId: productFull?.ownerId,
          qaSpecName,
          qaSpecFieldName,
          qaComment: comment.qaComment,
          qaUpdatedBy: userInfo?.id,
        };
      } else {
        payload = {
          id: comment.id,
          qaComment: comment.qaComment,
          qaUpdatedBy: userInfo?.id,
        };
      }
    }

    if (payload) {
      setIsTableUpdating(true);
      saveComment(payload);
    }
  };

  const hasEditCommentPermission = useCheckAllowEditComments();

  const handleOpenModal = () => {
    setDateRange([]);
    setPendingDateRange([]);
    setVisibleComment(true);
  };

  const handleSearch = () => {
    setDateRange(pendingDateRange);
  };

  const isAddDisabled =
    isSuperAdmin ||
    localComments.some((comment) => comment.isNew) ||
    (!isMemberSupplierUser && (!isQAUser || qaSpecStatus !== 'Submitted')) ||
    isDateFiltered;

  const isInvalidField =
    typeof qaSpecFieldName !== 'string' || qaSpecFieldName.trim() === '';

  if (!hasEditCommentPermission || isInvalidField) {
    return children;
  }

  return (
    <>
      {children}
      <TraceableCommentAdd onClick={handleOpenModal} />
      {visibleComment && (
        <TraceableCommentModal
          visible={visibleComment}
          onCancel={() => setVisibleComment(false)}
        >
          <TraceableCommentGridHeader
            treeData={treeData}
            dateRange={pendingDateRange}
            onDateRangeChange={setPendingDateRange}
            onSearch={handleSearch}
            disabled={isLoading || isTableUpdating}
          >
            <FormAddButton
              text='Add'
              onClick={handleAddComment}
              disabled={isAddDisabled}
            />
          </TraceableCommentGridHeader>
          <div
            className='traceable-comment-table-wrapper'
            style={{ paddingTop: 6 }}
          >
            <Spin spinning={isLoading || isTableUpdating}>
              <TraceableCommentTable
                data={localComments}
                refetch={refetch}
                setLocalComments={setLocalComments}
                handleCancel={handleCancelEdit}
                isQAUser={isQAUser}
                isMemberSupplierUser={isMemberSupplierUser}
                isSuperAdmin={isSuperAdmin}
                onSaveComment={handleSaveComment}
                qaSpecStatus={qaSpecStatus}
                disableActions={isDateFiltered}
              />
            </Spin>
          </div>
        </TraceableCommentModal>
      )}
    </>
  );
};

export default TraceableCommentContainer;
